import React, {Component} from "react";
import {connect} from "react-redux";
import PropTypes from "prop-types";
import {bindActionCreators} from "redux";
import {adjustStocks, getStockTake, searchStockTakeDiscrepancies} from "./actions";
import ListView from "../common/ListView";
import DateTimeDisplay from "../common/DateTimeDisplay";
import {Button, Icon, notification} from "antd";

import "./StockTakeDiscrepanciesPage.css";
import {decodeParams} from "../utils/params";

function getDiscrepancyKey({productId, sku}) {
  return productId + '`' + sku;
}

class StockTakeDiscrepanciesPage extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    /* state */
    searchDiscrepancies: PropTypes.object.isRequired,
    adjustingStocks: PropTypes.object.isRequired,
    stockTake: PropTypes.object,
    /* actions */
    searchStockTakeDiscrepancies: PropTypes.func.isRequired,
    adjustStocks: PropTypes.func.isRequired,
    getStockTake: PropTypes.func.isRequired,
  };

  state = {
    selectedRowKeys: [],
    adjustableDiscrepancyKeys: [],
    adjustedDiscrepancyKeys: [],
    bulkWorkflow: false,
  };

  componentDidMount() {
    this._load();
    this.setState({
      bulkWorkflow: this.props.location.search.includes('workflow=bulk'),
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.stockTakeId !== this.props.match.params.stockTakeId) this._load();
  }

  _next = () => {
    const {match: {params: {stockTakeId}}} = this.props;
    this.props.history.replace(`/stock-take/history/${stockTakeId}?workflow=bulk`);
  }

  _load = () => {
    const {getStockTake, match: {params}} = this.props;
    const {stockTakeId} = decodeParams(params);
    getStockTake({stockTakeId});
  };

  handleRowSelectionChange = (selectedRowKeys) => {
    const {adjustableDiscrepancyKeys, adjustedDiscrepancyKeys} = this.props.searchDiscrepancies.results.reduce(
      (
        {adjustableDiscrepancyKeys, adjustedDiscrepancyKeys},
        discrepancy
      ) => {
        const key = getDiscrepancyKey(discrepancy);
        if (selectedRowKeys.includes(key)) {
          if (discrepancy.adjusted) {
            adjustedDiscrepancyKeys = [...adjustedDiscrepancyKeys, key];
          } else {
            adjustableDiscrepancyKeys = [...adjustableDiscrepancyKeys, key];
          }
        }
        return {adjustableDiscrepancyKeys, adjustedDiscrepancyKeys};
      },
      {adjustableDiscrepancyKeys: [], adjustedDiscrepancyKeys: []}
    );
    this.setState({selectedRowKeys, adjustableDiscrepancyKeys, adjustedDiscrepancyKeys});
  };

  handleBatchAdjust = async() => {
    const {adjustStocks, searchDiscrepancies: {results}, match: {params}} = this.props;
    const {stockTakeId} = decodeParams(params);
    const selectedRowKeys = this.state.selectedRowKeys;
    try {
      const {stockToAdjust, alreadyAdjusted} = results.reduce(
        (
          {stockToAdjust, alreadyAdjusted},
          result
        ) => {
          if (selectedRowKeys.includes(getDiscrepancyKey(result))) {
            if (result.adjusted) {
              alreadyAdjusted = [...alreadyAdjusted, result];
            } else {
              const {productId, sku, expectedQuantity, actualQuantity} = result;
              const quantity = actualQuantity - expectedQuantity;
              const reason = `Stock-take #${stockTakeId}`;
              stockToAdjust = [...stockToAdjust, {productId, sku, quantity, reason}];
            }
          }
          return {stockToAdjust, alreadyAdjusted};
        },
        {stockToAdjust: [], alreadyAdjusted: []}
      );
      this.setState({alreadyAdjusted});
      await adjustStocks(stockToAdjust);
      const errors = this.props.adjustingStocks.errors;
      this.setState({selectedRowKeys: selectedRowKeys.filter(key => errors.findIndex(error => error.key === key) >= 0)});
      this.listView.refresh();
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: err.message
      });
    }
  };

  getCheckboxProps = (record) => {
    const {left, errors} = this.props.adjustingStocks;
    const recordKey = getDiscrepancyKey(record);
    let className;
    if (this.state.selectedRowKeys.includes(recordKey)) {
      if (left.find(key => key === recordKey)) {
        className = 'kokopelli-checkbox-in-progress';
      } else if (errors.find(error => error.key === recordKey)) {
        className = 'kokopelli-checkbox-error';
      }
    }
    return {className};
  };

  render() {
    const {
      history, searchStockTakeDiscrepancies, adjustingStocks, stockTake,
      searchDiscrepancies: {searching, pagination, filters, sorter, searchFilter, results},
      match: {params},
    } = this.props;
    const {stockTakeId} = decodeParams(params);
    const {bulkWorkflow, selectedRowKeys, adjustableDiscrepancyKeys, adjustedDiscrepancyKeys} = this.state;
    const rowSelection = stockTake && stockTake.endedAt === null
      ? {
        selectedRowKeys,
        fixed: true,
        onChange: this.handleRowSelectionChange,
        getCheckboxProps: this.getCheckboxProps,
      } : null;
    const columns = [
      {
        title: 'Product',
        dataIndex: 'productDisplay',
        key: 'productDisplay',
        sorter: true,
      },
      {
        title: 'SKU',
        dataIndex: 'sku',
        key: 'sku',
        sorter: true,
      },
      {
        title: 'Expected',
        dataIndex: 'expectedQuantity',
        key: 'expectedQuantity',
        sorter: true,
      },
      {
        title: 'Actual',
        dataIndex: 'actualQuantity',
        key: 'actualQuantity',
        sorter: true,
      },
      {
        title: 'Adjusted',
        dataIndex: 'adjusted',
        key: 'adjusted',
        sorter: true,
        render: adjusted => <Icon type={adjusted ? 'check' : 'close'} />
      },
      {
        title: 'Date',
        dataIndex: 'modifiedAt',
        key: 'modifiedAt',
        sorter: true,
        render: dateTime => <DateTimeDisplay dateTime={dateTime}/>,
      },
    ];
    const title = [];
    if (!bulkWorkflow) {
      title.push(<Button key="back" icon="arrow-left" onClick={() => history.goBack()} />);
    }
    title.push(" Discrepancies");
    return (
      <ListView
        key="list"
        ref={ref => this.listView = ref}
        title={title}
        loading={searching}
        columns={columns}
        dataSourceProvider={payload => searchStockTakeDiscrepancies({...payload, filters: {...payload.filters, stockTakeId}})}
        dataSource={results}
        pagination={pagination}
        searchFilter={searchFilter}
        filters={filters}
        sorter={sorter}
        rowKey={getDiscrepancyKey}
        scroll={{x: 850}}
        rowSelection={rowSelection}
        onRow={this.onRow}
        cardFooter={bulkWorkflow ? <Button key="next" type="primary" onClick={this._next}>Next</Button> : null}
      >
        {stockTake && stockTake.endedAt === null && [
          <Button
            key="adjust"
            type="primary"
            style={{marginBottom: '0.5em'}}
            disabled={adjustableDiscrepancyKeys.length === 0 || adjustingStocks.inProgress}
            onClick={this.handleBatchAdjust}
          >
            Adjust&nbsp;{adjustableDiscrepancyKeys.length}&nbsp;stocks
          </Button>,
          ' ',
          adjustedDiscrepancyKeys.length > 0 && `${adjustedDiscrepancyKeys.length} already adjusted.`,
          ' ',
          adjustingStocks.done.length > 0 && `${adjustingStocks.done.length} completed.`,
          ' ',
          adjustingStocks.errors.length > 0 && `${adjustingStocks.errors.length} failed.`
        ]}
      </ListView>
    );
  }
}

const mapStateToProps = state => ({
  loading: state.stockTake.stockTakesLoading,
  searchDiscrepancies: state.stockTake.searchDiscrepancies,
  adjustingStocks: state.stockTake.adjustingStocks,
  stockTake: state.stockTake.stockTake,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  searchStockTakeDiscrepancies,
  adjustStocks,
  getStockTake,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(StockTakeDiscrepanciesPage);



// WEBPACK FOOTER //
// ./src/StockTake/StockTakeDiscrepanciesPage.js